import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Lock, Mail, Smartphone, Star, ArrowDown, Gift, Heart, ShieldCheck, XCircle, MapPin } from 'lucide-react';
const CHECKOUT_URL = 'https://impulsodigital.vip/cart/44739342434390:1';
const IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/q9qu6YoZBLxv4ak3GVLkee/ChatGPT_Image_18_may_2026,_00_02_07.png';
const CTAButton = ({
  text,
  className = ''
}: {
  text: string;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`block w-full max-w-xl mx-auto bg-brand-pink hover:bg-brand-pinkHover text-white font-bold text-xl md:text-3xl py-5 md:py-6 px-6 rounded-xl shadow-[0_10px_25px_rgba(236,72,153,0.5)] transition-all transform hover:scale-105 text-center animate-pulse-fast border-b-4 border-pink-800 ${className}`} data-id="element-0">
    {text}
  </a>;
const CheckBullet = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`flex items-start gap-3 ${className}`} data-id="element-1">
    <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-green-500 flex-shrink-0 mt-1" data-id="element-2" />
    <p className="text-lg md:text-xl font-medium leading-relaxed text-gray-800" data-id="element-3">
      {children}
    </p>
  </div>;
const CrossBullet = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="flex items-start gap-3" data-id="element-4">
    <XCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500 flex-shrink-0 mt-1" data-id="element-5" />
    <p className="text-lg md:text-xl font-medium leading-relaxed text-gray-800" data-id="element-6">
      {children}
    </p>
  </div>;
const TestimonialCard = ({
  quote,
  name,
  country
}: {
  quote: string;
  name: string;
  country: string;
}) => <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100 flex flex-col h-full" data-id="element-7">
    <div className="flex gap-1 mb-4" data-id="element-8">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-9" />)}
    </div>
    <p className="text-gray-700 text-lg italic mb-6 flex-grow" data-id="element-10">"{quote}"</p>
    <div className="flex items-center gap-2 mt-auto pt-4 border-t border-gray-100" data-id="element-11">
      <div className="w-10 h-10 rounded-full bg-brand-pink/10 flex items-center justify-center text-brand-pink font-bold" data-id="element-12">
        {name.charAt(0)}
      </div>
      <div data-id="element-13">
        <p className="font-bold text-gray-900" data-id="element-14">{name}</p>
        <p className="text-sm text-gray-500" data-id="element-15">{country}</p>
      </div>
    </div>
  </div>;
const PurchasePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    // Show popup after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 5000);
    // Hide after 10 seconds of being visible
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 15000);
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);
  return <AnimatePresence data-id="element-16">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 50,
      x: -20
    }} className="fixed bottom-4 left-4 md:bottom-8 md:left-8 z-50 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 max-w-[300px] flex items-start gap-3" data-id="element-17">
          <div className="bg-green-100 p-2 rounded-full flex-shrink-0" data-id="element-18">
            <MapPin className="w-5 h-5 text-green-600" data-id="element-19" />
          </div>
          <div data-id="element-20">
            <p className="text-sm font-medium text-gray-900" data-id="element-21">
              <span className="font-bold" data-id="element-22">Valentina</span> de{' '}
              <span className="font-bold" data-id="element-23">Chile</span>
            </p>
            <p className="text-xs text-gray-600 mt-1" data-id="element-24">
              Acaba de comprar este programa digital
            </p>
            <p className="text-[10px] text-gray-400 mt-1" data-id="element-25">Hace 2 minutos</p>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
export function App() {
  return <div className="min-h-screen bg-[#fcfcfc] overflow-x-hidden selection:bg-brand-pink selection:text-white" data-id="element-26">
      <PurchasePopup data-id="element-27" />

      {/* HERO SECTION */}
      <section className="pt-12 pb-16 md:pt-20 md:pb-24 px-4 max-w-5xl mx-auto text-center" data-id="element-28">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-8 tracking-tight" data-id="element-29">
          👉 110 sesiones para dejar de postergarte, poner límites sin culpa y{' '}
          <br className="hidden md:block" data-id="element-30" />
          <span className="font-handwriting text-5xl md:text-7xl lg:text-8xl text-brand-pink block mt-2 transform -rotate-2" data-id="element-31">
            empezar a elegirte de verdad
          </span>
        </h1>

        <a href={CHECKOUT_URL} className="block w-full max-w-3xl mx-auto mb-10 transform hover:scale-[1.02] transition-transform duration-300" data-id="element-32">
          <img src={IMAGE_URL} alt="Programa Elegirme Primero y Bonos" className="w-full h-auto object-contain drop-shadow-2xl rounded-xl" data-id="element-33" />
        </a>

        <h2 className="text-xl md:text-2xl font-medium text-gray-700 max-w-3xl mx-auto mb-6 leading-relaxed" data-id="element-34">
          Un programa digital creado para ayudarte a sanar patrones emocionales,
          recuperar tu autoestima y dejar de abandonarte a vos misma.
        </h2>

        <p className="text-lg md:text-xl font-bold text-brand-pink mb-10 bg-pink-50 inline-block px-6 py-2 rounded-full border border-pink-200" data-id="element-35">
          ✨ Incluye acceso inmediato al programa completo + 6 libros digitales
          de regalo.
        </p>

        <div className="max-w-2xl mx-auto text-left space-y-4 mb-12 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-36">
          <CheckBullet data-id="element-37">Aprendé a decir que no sin culpa</CheckBullet>
          <CheckBullet data-id="element-38">
            Soltá relaciones y situaciones que te lastiman
          </CheckBullet>
          <CheckBullet data-id="element-39">Dejá de ponerte última todo el tiempo</CheckBullet>
          <CheckBullet data-id="element-40">Entendé por qué repetís los mismos patrones</CheckBullet>
          <CheckBullet data-id="element-41">
            Empezá a construir una versión más segura de vos
          </CheckBullet>
        </div>

        <div className="mb-10" data-id="element-42">
          <p className="text-2xl md:text-3xl text-gray-400 font-bold line-through mb-2" data-id="element-43">
            Precio Normal: $49.990
          </p>
          <div className="inline-block transform -rotate-1" data-id="element-44">
            <p className="text-4xl md:text-6xl font-black text-gray-900 bg-brand-yellow px-4 py-2 rounded-lg shadow-sm" data-id="element-45">
              👉 HOY SOLO $19.990 ARS
            </p>
          </div>
          <p className="text-lg font-medium text-gray-600 mt-4" data-id="element-46">
            ✨ Acceso inmediato en formato digital
          </p>
        </div>

        <div className="flex justify-center mb-8" data-id="element-47">
          <ArrowDown className="w-12 h-12 text-brand-pink animate-bounce" data-id="element-48" />
        </div>

        <CTAButton text="👉 QUIERO EMPEZAR A ELEGIRME" data-id="element-49" />

        <div className="flex flex-wrap justify-center gap-6 mt-8 text-gray-600 font-medium text-sm md:text-base" data-id="element-50">
          <div className="flex items-center gap-2" data-id="element-51">
            <Lock className="w-5 h-5 text-green-600" data-id="element-52" /> Compra segura
          </div>
          <div className="flex items-center gap-2" data-id="element-53">
            <Mail className="w-5 h-5 text-blue-600" data-id="element-54" /> Acceso inmediato
          </div>
          <div className="flex items-center gap-2" data-id="element-55">
            <Smartphone className="w-5 h-5 text-purple-600" data-id="element-56" /> Compatible con
            celular y PC
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 md:py-24 bg-red-50 px-4 border-y border-red-100" data-id="element-57">
        <div className="max-w-4xl mx-auto" data-id="element-58">
          <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-12" data-id="element-59">
            Si sentís que…
          </h2>

          <div className="space-y-6 max-w-2xl mx-auto bg-white p-8 md:p-10 rounded-3xl shadow-xl" data-id="element-60">
            <p className="text-xl md:text-2xl font-medium text-gray-800 flex items-center gap-4" data-id="element-61">
              <span className="text-3xl" data-id="element-62">😔</span> Estás emocionalmente agotada
            </p>
            <p className="text-xl md:text-2xl font-medium text-gray-800 flex items-center gap-4" data-id="element-63">
              <span className="text-3xl" data-id="element-64">💔</span> Te cuesta soltar aunque sabés
              que deberías
            </p>
            <p className="text-xl md:text-2xl font-medium text-gray-800 flex items-center gap-4" data-id="element-65">
              <span className="text-3xl" data-id="element-66">🥺</span> Siempre terminás poniéndote
              última
            </p>
            <p className="text-xl md:text-2xl font-medium text-gray-800 flex items-center gap-4" data-id="element-67">
              <span className="text-3xl" data-id="element-68">🔁</span> Repetís los mismos patrones
              una y otra vez
            </p>
            <p className="text-xl md:text-2xl font-medium text-gray-800 flex items-center gap-4" data-id="element-69">
              <span className="text-3xl" data-id="element-70">😣</span> Te cuesta poner límites sin
              sentir culpa
            </p>
          </div>

          <p className="text-2xl md:text-4xl font-bold text-center mt-12 text-gray-900" data-id="element-71">
            Entonces este programa fue{' '}
            <span className="text-brand-pink underline decoration-4 underline-offset-4" data-id="element-72">
              creado para vos.
            </span>
          </p>
        </div>
      </section>

      {/* EMOTIONAL BLOCK (BLACK BACKGROUND) */}
      <section className="py-20 md:py-32 bg-brand-dark text-white px-4 relative overflow-hidden" data-id="element-73">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-74">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-pink rounded-full blur-[120px]" data-id="element-75"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600 rounded-full blur-[120px]" data-id="element-76"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-77">
          <h2 className="text-4xl md:text-6xl font-black mb-8 text-white" data-id="element-78">
            No es que no puedas.
          </h2>
          <p className="text-2xl md:text-4xl font-bold leading-tight mb-16 text-gray-200" data-id="element-79">
            👉 Es que{' '}
            <span className="text-brand-yellow" data-id="element-80">
              nadie te enseñó cómo elegirte
            </span>{' '}
            sin sentir que estás lastimando a los demás.
          </p>

          <p className="text-xl md:text-2xl text-gray-400 mb-8 font-medium" data-id="element-81">
            Durante años aprendiste a:
          </p>

          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 mb-16" data-id="element-82">
            <span className="bg-white/10 px-6 py-3 rounded-lg text-xl font-bold border border-white/20" data-id="element-83">
              aguantar
            </span>
            <span className="bg-white/10 px-6 py-3 rounded-lg text-xl font-bold border border-white/20" data-id="element-84">
              callarte
            </span>
            <span className="bg-white/10 px-6 py-3 rounded-lg text-xl font-bold border border-white/20" data-id="element-85">
              priorizar a todos
            </span>
            <span className="bg-white/10 px-6 py-3 rounded-lg text-xl font-bold border border-white/20" data-id="element-86">
              postergarte
            </span>
          </div>

          <p className="text-2xl md:text-4xl font-handwriting text-brand-pink" data-id="element-87">
            Y eso termina destruyendo tu autoestima sin que te des cuenta.
          </p>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 md:py-24 px-4 bg-white" data-id="element-88">
        <div className="max-w-5xl mx-auto" data-id="element-89">
          <div className="text-center mb-16" data-id="element-90">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4" data-id="element-91">
              ✨ "Mi Psicóloga Me Dijo"
            </h2>
            <p className="text-2xl md:text-3xl font-handwriting text-brand-pink" data-id="element-92">
              Programa Elegirme Primero
            </p>
          </div>

          <p className="text-xl md:text-2xl text-center font-medium text-gray-700 mb-12 max-w-3xl mx-auto" data-id="element-93">
            Es una guía emocional diseñada para ayudarte a:
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12" data-id="element-94">
            <div className="bg-pink-50 p-8 rounded-2xl border border-pink-100" data-id="element-95">
              <div className="space-y-6" data-id="element-96">
                <CheckBullet data-id="element-97">Recuperar tu valor personal</CheckBullet>
                <CheckBullet data-id="element-98">Sanar vínculos que te lastiman</CheckBullet>
                <CheckBullet data-id="element-99">Dejar de depender emocionalmente</CheckBullet>
              </div>
            </div>
            <div className="bg-pink-50 p-8 rounded-2xl border border-pink-100" data-id="element-100">
              <div className="space-y-6" data-id="element-101">
                <CheckBullet data-id="element-102">Entender tus patrones emocionales</CheckBullet>
                <CheckBullet data-id="element-103">Volver a conectar con vos</CheckBullet>
              </div>
            </div>
          </div>

          <p className="text-xl md:text-2xl text-center font-bold text-gray-900 bg-brand-yellow inline-block mx-auto px-6 py-2 transform -rotate-1 shadow-sm" data-id="element-104">
            Todo explicado de forma simple, cercana y práctica.
          </p>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-16 md:py-24 px-4 bg-gray-50 border-t border-gray-200" data-id="element-105">
        <div className="max-w-4xl mx-auto" data-id="element-106">
          <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-12 flex items-center justify-center gap-4" data-id="element-107">
            <span className="text-4xl" data-id="element-108">🌷</span> ¿Qué va a cambiar en tu vida?
          </h2>

          <div className="space-y-6 bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100" data-id="element-109">
            <CheckBullet className="items-center" data-id="element-110">
              Lograrás dejar de cargar con problemas que no son tuyos
            </CheckBullet>
            <div className="w-full h-px bg-gray-100" data-id="element-111"></div>
            <CheckBullet className="items-center" data-id="element-112">
              Vas a aprender a soltar sin sentir culpa
            </CheckBullet>
            <div className="w-full h-px bg-gray-100" data-id="element-113"></div>
            <CheckBullet className="items-center" data-id="element-114">
              Dejarás de postergarte constantemente
            </CheckBullet>
            <div className="w-full h-px bg-gray-100" data-id="element-115"></div>
            <CheckBullet className="items-center" data-id="element-116">
              Entenderás por qué repetís las mismas historias
            </CheckBullet>
            <div className="w-full h-px bg-gray-100" data-id="element-117"></div>
            <CheckBullet className="items-center" data-id="element-118">
              Vas a construir una versión de vos con más paz y autoestima
            </CheckBullet>
          </div>

          {/* MID PAGE CTA */}
          <div className="mt-16 text-center" data-id="element-119">
            <div className="flex justify-center mb-6" data-id="element-120">
              <ArrowDown className="w-10 h-10 text-brand-pink animate-bounce" data-id="element-121" />
            </div>
            <CTAButton text="👉 SÍ, QUIERO TRANSFORMAR MI VIDA HOY" data-id="element-122" />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / TESTIMONIALS */}
      <section className="py-16 md:py-24 px-4 bg-white" data-id="element-123">
        <div className="max-w-6xl mx-auto" data-id="element-124">
          <div className="text-center mb-16" data-id="element-125">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4" data-id="element-126">
              Miles de mujeres ya empezaron a elegirse 💖
            </h2>
            <p className="text-xl text-gray-600 font-medium" data-id="element-127">
              Historias reales de transformación emocional
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-128">
            {/* Original 4 */}
            <TestimonialCard quote="Por primera vez pude decir que no sin sentirme mala persona." name="Cristina" country="España" data-id="element-129" />
            <TestimonialCard quote="Sentí que alguien finalmente entendía lo que me pasaba." name="Sofía" country="México" data-id="element-130" />
            <TestimonialCard quote="Me ayudó a soltar una relación que me hacía mal hace años." name="Paulina" country="Argentina" data-id="element-131" />
            <TestimonialCard quote="Dejé de repetir historias que me destruían emocionalmente." name="María" country="Colombia" data-id="element-132" />

            {/* Added 5 */}
            <TestimonialCard quote="Me di cuenta de lo mucho que me estaba abandonando. Este programa fue un abrazo al alma." name="Valentina" country="Chile" data-id="element-133" />
            <TestimonialCard quote="Aprender a poner límites me aterraba, pero las sesiones me dieron la fuerza que necesitaba." name="Camila" country="Perú" data-id="element-134" />
            <TestimonialCard quote="Lloré, sané y por fin me siento en paz conmigo misma. Lo recomiendo a todas." name="Lucía" country="Uruguay" data-id="element-135" />
            <TestimonialCard quote="Es exactamente lo que necesitaba leer. Me siento comprendida y mucho más fuerte." name="Andrea" country="Venezuela" data-id="element-136" />
            <TestimonialCard quote="Dejé de sentir culpa por decir que no. Mi vida cambió por completo." name="Daniela" country="Ecuador" data-id="element-137" />
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 md:py-24 px-4 bg-pink-50 border-y-4 border-dashed border-pink-200" data-id="element-138">
        <div className="max-w-5xl mx-auto text-center" data-id="element-139">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded-full font-bold text-lg md:text-xl mb-8 shadow-lg animate-pulse" data-id="element-140">
            <Gift className="w-6 h-6" data-id="element-141" /> GRATIS SOLO POR HOY
          </div>

          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-12" data-id="element-142">
            🎁 SOLO POR HOY RECIBÍS <br className="hidden md:block" data-id="element-143" />
            <span className="text-brand-pink" data-id="element-144">
              6 LIBROS DIGITALES DE REGALO
            </span>
          </h2>

          <a href={CHECKOUT_URL} className="block w-full max-w-3xl mx-auto mb-12 transform hover:scale-[1.02] transition-transform duration-300" data-id="element-145">
            <img src={IMAGE_URL} alt="6 Libros Digitales de Regalo" className="w-full h-auto object-contain drop-shadow-2xl rounded-xl" data-id="element-146" />
          </a>

          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl max-w-3xl mx-auto text-left" data-id="element-147">
            <h3 className="text-2xl font-bold text-center mb-8 text-gray-900" data-id="element-148">
              ✨ HOY TE LLEVÁS TAMBIÉN:
            </h3>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-149">
              <a href={CHECKOUT_URL} className="relative flex items-center gap-4 text-lg font-medium text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-brand-pink hover:shadow-md transition-all" data-id="element-150">
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow uppercase tracking-wider" data-id="element-151">
                  Gratis
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/4TEndXpuhpLsFhntSuNVwB/ChatGPT_Image_18_may_2026,_00_31_01.png" alt="Hoy Te Dejo Ir - Libro Digital de Regalo" className="w-16 h-20 object-cover rounded-md shadow-md flex-shrink-0" data-id="element-152" />
                <span className="flex-1" data-id="element-153">Hoy Te Dejo Ir</span>
              </a>
              <a href={CHECKOUT_URL} className="relative flex items-center gap-4 text-lg font-medium text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-brand-pink hover:shadow-md transition-all" data-id="element-154">
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow uppercase tracking-wider" data-id="element-155">
                  Gratis
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/wb6nkm8eBqpDX1vJ6Ek81h/ChatGPT_Image_18_may_2026,_00_32_26.png" alt="Soltar para Sanar - Libro Digital de Regalo" className="w-16 h-20 object-cover rounded-md shadow-md flex-shrink-0" data-id="element-156" />
                <span className="flex-1" data-id="element-157">Soltar para Sanar</span>
              </a>
              <a href={CHECKOUT_URL} className="relative flex items-center gap-4 text-lg font-medium text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-brand-pink hover:shadow-md transition-all" data-id="element-158">
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow uppercase tracking-wider" data-id="element-159">
                  Gratis
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/eH2hLVxvufa4bskqsayLBy/ChatGPT_Image_18_may_2026,_00_35_14.png" alt="Tú Eres Tu Prioridad - Libro Digital de Regalo" className="w-16 h-20 object-cover rounded-md shadow-md flex-shrink-0" data-id="element-160" />
                <span className="flex-1" data-id="element-161">Tú Eres Tu Prioridad</span>
              </a>
              <a href={CHECKOUT_URL} className="relative flex items-center gap-4 text-lg font-medium text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-brand-pink hover:shadow-md transition-all" data-id="element-162">
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow uppercase tracking-wider" data-id="element-163">
                  Gratis
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/wkuPTRsMaGpFv9dvd5eZCQ/ChatGPT_Image_18_may_2026,_00_36_43.png" alt="Vuelvo a Mí - Libro Digital de Regalo" className="w-16 h-20 object-cover rounded-md shadow-md flex-shrink-0" data-id="element-164" />
                <span className="flex-1" data-id="element-165">Vuelvo a Mí</span>
              </a>
              <a href={CHECKOUT_URL} className="relative flex items-center gap-4 text-lg font-medium text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-brand-pink hover:shadow-md transition-all" data-id="element-166">
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow uppercase tracking-wider" data-id="element-167">
                  Gratis
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/twfEhgnFMWupYFNKye6FTX/ChatGPT_Image_18_may_2026,_00_37_44.png" alt="Si Lo Crees, Lo Creas - Libro Digital de Regalo" className="w-16 h-20 object-cover rounded-md shadow-md flex-shrink-0" data-id="element-168" />
                <span className="flex-1" data-id="element-169">Si Lo Crees, Lo Creas</span>
              </a>
              <a href={CHECKOUT_URL} className="relative flex items-center gap-4 text-lg font-medium text-gray-800 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-brand-pink hover:shadow-md transition-all" data-id="element-170">
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow uppercase tracking-wider" data-id="element-171">
                  Gratis
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/5ErPKRZ4oMZLpnkbWsM2Ud/ChatGPT_Image_18_may_2026,_00_39_11.png" alt="Merezco Mucho Más - Libro Digital de Regalo" className="w-16 h-20 object-cover rounded-md shadow-md flex-shrink-0" data-id="element-172" />
                <span className="flex-1" data-id="element-173">Merezco Mucho Más</span>
              </a>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start gap-4" data-id="element-174">
              <span className="text-2xl" data-id="element-175">💡</span>
              <p className="text-gray-700 font-medium" data-id="element-176">
                +6 libros digitales adicionales en formato PDF para acompañar tu
                proceso de transformación emocional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IDENTIFICATION BLOCK */}
      <section className="py-16 md:py-24 px-4 bg-white" data-id="element-177">
        <div className="max-w-4xl mx-auto" data-id="element-178">
          <div className="text-center mb-12" data-id="element-179">
            <span className="text-4xl mb-4 block" data-id="element-180">⚠️</span>
            <h2 className="text-2xl md:text-4xl font-black text-gray-900" data-id="element-181">
              Este programa{' '}
              <span className="text-red-600 underline decoration-4" data-id="element-182">NO</span> es
              para personas que quieren seguir igual.
            </h2>
          </div>

          <div className="bg-gray-50 p-8 md:p-12 rounded-3xl border border-gray-200 shadow-sm" data-id="element-183">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-184">
              Es para mujeres que:
            </h3>
            <div className="space-y-6 max-w-2xl mx-auto" data-id="element-185">
              <CheckBullet data-id="element-186">están cansadas de sufrir en silencio</CheckBullet>
              <CheckBullet data-id="element-187">quieren recuperar su autoestima</CheckBullet>
              <CheckBullet data-id="element-188">desean sanar emocionalmente</CheckBullet>
              <CheckBullet data-id="element-189">quieren empezar a elegirse de verdad</CheckBullet>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL OFFER (BLACK BACKGROUND) */}
      <section className="py-16 md:py-24 px-4 bg-brand-dark text-white border-t-8 border-brand-pink" data-id="element-190">
        <div className="max-w-4xl mx-auto" data-id="element-191">
          <div className="text-center mb-12" data-id="element-192">
            <h2 className="text-3xl md:text-5xl font-black mb-4 flex items-center justify-center gap-3" data-id="element-193">
              <span className="text-brand-yellow" data-id="element-194">💎</span> OFERTA FINAL
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 font-medium" data-id="element-195">
              ✨ TODO ESTO HOY POR SOLO:
            </p>
          </div>

          <div className="bg-white text-gray-900 p-8 md:p-12 rounded-3xl shadow-2xl max-w-2xl mx-auto mb-12 transform md:scale-105 relative" data-id="element-196">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-8 md:px-10 py-2 rounded-full font-bold text-xs md:text-sm uppercase tracking-wider shadow-lg whitespace-nowrap" data-id="element-197">
              ⚡ Oferta por tiempo limitado ⚡
            </div>
            <div className="h-3" data-id="element-198"></div>

            <div className="text-center mb-8 border-b border-gray-200 pb-8" data-id="element-199">
              <p className="text-3xl text-gray-400 font-bold line-through mb-2" data-id="element-200">
                $49.990
              </p>
              <p className="text-5xl md:text-7xl font-black text-brand-pink mb-4" data-id="element-201">
                👉 $19.990 ARS
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-202">
              <CheckBullet className="text-base md:text-lg" data-id="element-203">
                Programa completo "Mi Psicóloga Me Dijo"
              </CheckBullet>
              <CheckBullet className="text-base md:text-lg" data-id="element-204">
                Acceso inmediato
              </CheckBullet>
              <CheckBullet className="text-base md:text-lg" data-id="element-205">
                <span className="font-bold text-brand-pink" data-id="element-206">
                  6 libros digitales de regalo
                </span>
              </CheckBullet>
              <CheckBullet className="text-base md:text-lg" data-id="element-207">
                Formato PDF
              </CheckBullet>
              <CheckBullet className="text-base md:text-lg" data-id="element-208">
                Disponible para celular y computadora
              </CheckBullet>
            </div>

            <div className="flex justify-center mb-6" data-id="element-209">
              <ArrowDown className="w-10 h-10 text-brand-pink animate-bounce" data-id="element-210" />
            </div>

            <CTAButton text="👉 QUIERO EMPEZAR MI CAMBIO HOY" className="w-full max-w-full text-lg md:text-2xl py-4 md:py-5" data-id="element-211" />

            <p className="text-center text-red-600 font-bold mt-6 flex items-center justify-center gap-2" data-id="element-212">
              <span className="text-xl" data-id="element-213">⚠️</span> Oferta disponible por tiempo
              limitado.
            </p>
          </div>

          {/* SECURITY / CLOSING */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-gray-300 font-medium border-t border-white/10 pt-12" data-id="element-214">
            <div className="flex items-center gap-3" data-id="element-215">
              <ShieldCheck className="w-8 h-8 text-green-400" data-id="element-216" />
              <span data-id="element-217">Pago 100% seguro</span>
            </div>
            <div className="flex items-center gap-3" data-id="element-218">
              <Mail className="w-8 h-8 text-blue-400" data-id="element-219" />
              <span data-id="element-220">Descarga inmediata</span>
            </div>
            <div className="flex items-center gap-3" data-id="element-221">
              <Smartphone className="w-8 h-8 text-purple-400" data-id="element-222" />
              <span data-id="element-223">Compatible con cualquier dispositivo</span>
            </div>
          </div>
        </div>
      </section>
    </div>;
}